import { createFileRoute, redirect } from '@tanstack/react-router';

import { captureAnalyticsEvent } from '#utils/analytics/analytics-events.js';
import { toSignupPersonProperties } from '#utils/analytics/campaign-tags.js';
import { auth0AppStateSchema } from '#utils/auth0-app-state.js';
import { readCampaignClaim, resolveSignupCampaign } from '#utils/auth0-campaign.js';
import { auth0UserQueryKey } from '#utils/auth0-user-query-options.js';
import {
	getLoginDenialDestination,
	getLoginDenialReason,
	getLoginDenialSubject,
	LoginDenialReason,
} from './auth0-callback-denial.js';
import { Auth0CallbackError } from './auth0-callback-error.js';
import { isUnprovisionedUserError } from './auth0-callback-provisioning.js';
import { recordSignIn } from './auth0-callback-sign-in.js';
import { storePendingVerificationSubject } from './pending-email-verification.js';

import type { ConnectAccountRedirectResult, RedirectLoginResult } from '@auth0/auth0-spa-js';

export const Route = createFileRoute('/_authorization-gate/callback')({
	onError: (error: unknown) => {
		if (
			typeof error === 'object' &&
			error !== null &&
			'routerCode' in error &&
			error.routerCode === 'VALIDATE_SEARCH'
		) {
			throw redirect({ to: '/', replace: true });
		}
	},
	beforeLoad: async ({ context: { auth0Client, queryClient, trpc, trpcClient } }) => {
		let redirectResult: RedirectLoginResult<unknown> | ConnectAccountRedirectResult<unknown>;
		try {
			redirectResult = await auth0Client.handleRedirectCallback<unknown>();
		} catch (error) {
			// A Post-Login Action denied the login. Route known denials to their
			// dedicated screen; rethrow anything else so it surfaces as a fault.
			const reason = getLoginDenialReason(error);
			if (reason === null) {
				throw error;
			}

			if (reason === LoginDenialReason.EmailUnverified) {
				const subject = getLoginDenialSubject(error);
				if (subject !== null) {
					storePendingVerificationSubject(subject);
				}
			}
			throw redirect({ to: getLoginDenialDestination(reason), replace: true });
		}

		// Refresh the Auth0 user profile now that the login has completed.
		void queryClient.invalidateQueries({ queryKey: auth0UserQueryKey });

		// Parsed up front because the signup event below is attributed from it; the callback URL itself carries no
		// campaign tags, having come from Auth0 rather than from the marketing link.
		const { returnTo, campaign: appStateCampaign } = auth0AppStateSchema.parse(redirectResult.appState);

		// Provision only on a genuine first login — a valid token with no usable
		// account yet: `getCurrent` rejects for an unknown user, or resolves to a
		// user with no org memberships. Existing users, including staff (who resolve
		// to a session via the Auth0 org claim), already have an account and skip
		// provisioning rather than running the create-or-join write on every login.
		let hasProvisionedAccount = false;
		try {
			const currentUser = await queryClient.fetchQuery(trpc.user.getCurrent.queryOptions());
			hasProvisionedAccount = currentUser.memberships.length > 0;
		} catch (error) {
			// Only a genuine "no local account yet" (UNAUTHORIZED) means the user
			// needs provisioning. Any other failure is a real fault — surface it
			// rather than masking it with an unnecessary provisioning write.
			if (!isUnprovisionedUserError(error)) {
				throw error;
			}
			hasProvisionedAccount = false;
		}

		if (!hasProvisionedAccount) {
			const provisioned = await trpcClient.provisioning.provision.mutate();
			// From the token rather than the local account: the signup fires before the app shell identifies the person,
			// so the event has to name the user itself for a CRM to key a contact on.
			const claims = await auth0Client.getIdTokenClaims();
			const campaign = resolveSignupCampaign(readCampaignClaim(claims), appStateCampaign);
			const personProperties = toSignupPersonProperties(campaign);
			captureAnalyticsEvent('user_signed_up', {
				org_name: provisioned.organizationUsername,
				org_created: provisioned.created,
				role: provisioned.role,
				...(claims?.email !== undefined && { email: claims.email }),
				...campaign,
				...(Object.keys(personProperties).length > 0 && { $set_once: personProperties }),
			});
			await queryClient.invalidateQueries({ queryKey: trpc.user.getCurrent.queryKey() });
		}

		// Recorded after provisioning, so a first-time signer has an account to
		// record it against.
		await recordSignIn(trpcClient);

		if (returnTo) {
			throw redirect({ href: returnTo, replace: true });
		} else {
			throw redirect({ to: '/', replace: true });
		}
	},
	errorComponent: Auth0CallbackError,
	component: Auth0CallbackPage,
});

function Auth0CallbackPage() {
	return null;
}
